import { FC, MouseEventHandler, useEffect, useRef, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { useParams } from "react-router-dom";
import { DateTime, Interval } from "luxon";
import Color from "color";

import { classNames } from "@/shared/utils/classnames";
import { BaseEvent } from "@/app/events/events";
import { colors } from "@/app/fees/fees";

export type CalendarDayProps = {
  currentDateTime: DateTime;
  setCurrentDateTime: (dateTime: DateTime) => void;
  events: Array<BaseEvent>;
  onEventClick: (event: BaseEvent) => void;
  onNextMonth: MouseEventHandler;
  onPrevMonth: MouseEventHandler;
};

// Helper function to find the first event of the current day
export const getFirstEventMinute = (days: any, items: any): number => {
  const currentDay = days?.find((day) => day?.isCurrentDay);
  if (!currentDay || !items[currentDay.key]) {
    // If no events for today, default to 6 AM (360 minutes)
    return 360;
  }

  const todayEvents = items[currentDay.key];
  if (todayEvents.length === 0) {
    // If no events for today, default to 6 AM (360 minutes)
    return 360;
  }

  // Find the earliest start time
  const earliestEvent = todayEvents.reduce((earliest, current) => {
    const currentStartTime = DateTime.fromFormat(current.startTime, "HH:mm:ss");
    const earliestStartTime = DateTime.fromFormat(
      earliest.startTime,
      "HH:mm:ss"
    );

    return currentStartTime < earliestStartTime ? current : earliest;
  });

  // Convert start time to minutes from midnight
  const startTime = DateTime.fromFormat(earliestEvent.startTime, "HH:mm:ss");
  return startTime.hour * 60 + startTime.minute;
};

export const CalendarDay: FC<CalendarDayProps> = ({
  currentDateTime,
  events,
  onEventClick,
  onNextMonth,
  onPrevMonth,
  setCurrentDateTime,
}) => {
  const { id } = useParams();

  const container = useRef(null);
  const containerNav = useRef(null);
  const containerOffset = useRef(null);

  const [items, setItems] = useState<Record<string, any>>({});

  /*
  useEffect(() => {
    setItems(() => Object.groupBy(events, (event: Event) => event.date));
  }, [events]);
  */

  const groupBy = (array, keyGetter) => {
    return array.reduce((result, item) => {
      const key = keyGetter(item);
      if (!result[key]) {
        result[key] = [];
      }
      result[key].push(item);
      return result;
    }, {});
  };

  useEffect(() => {
    setItems(() => groupBy(events, (event: BaseEvent) => event.date));
  }, [events]);

  const [days, setDays] = useState([]);

  useEffect(() => {
    const startOfMonth = currentDateTime.startOf("month");
    const startOfWeek = startOfMonth.startOf("week");
    const endOfMonth = currentDateTime.endOf("month");
    const endOfWeek = endOfMonth.endOf("week");

    const today = DateTime.now().startOf("day");
    const todayFormatted = today.toFormat("yyyy-MM-dd");

    const interval = Interval.fromDateTimes(startOfWeek, endOfWeek);
    const days = interval.splitBy({ day: 1 }).map((interval) => {
      const dayFormatted = interval.start
        ?.startOf("day")
        .toFormat("yyyy-MM-dd");
      const isToday = dayFormatted === todayFormatted;

      return {
        date: interval.start,
        key: dayFormatted,
        number: interval.start?.toFormat("dd"),
        isToday,
        isCurrentMonth: interval.start?.hasSame(currentDateTime, "month"),
        isCurrentDay: interval.start?.hasSame(currentDateTime, "day"),
        events: [],
      };
    });

    setDays(days);
  }, [currentDateTime]);

  useEffect(() => {
    // Set the container scroll position based on the first event of the day
    if (container.current && containerNav.current && containerOffset.current) {
      const firstEventMinute = getFirstEventMinute(days, items);

      container.current.scrollTop =
        ((container.current.scrollHeight -
          containerNav.current.offsetHeight -
          containerOffset.current.offsetHeight) *
          firstEventMinute) /
        1440;
    }
  }, [days, items]); // Add dependencies to recalculate when days or events change
  return (
    <div className="isolate flex flex-auto overflow-hidden bg-white">
      <div ref={container} className="flex flex-auto flex-col overflow-auto">
        <div
          ref={containerNav}
          className="sticky top-0 z-10 grid flex-none grid-cols-7 bg-white text-xs text-gray-500 shadow ring-1 ring-black ring-opacity-5 md:hidden"
        >
          <button
            type="button"
            className="flex flex-col items-center pb-1.5 pt-3"
          >
            <span>M</span>
            {/* Default: "text-gray-900", Selected: "bg-gray-900 text-white", Today (Not Selected): "text-primary-600", Today (Selected): "bg-indigo-600 text-white" */}
            <span className="mt-3 flex h-8 w-8 items-center justify-center rounded-full text-base font-semibold text-gray-900">
              19
            </span>
          </button>
          <button
            type="button"
            className="flex flex-col items-center pb-1.5 pt-3"
          >
            <span>J</span>
            <span className="mt-3 flex h-8 w-8 items-center justify-center rounded-full text-base font-semibold text-primary-600">
              20
            </span>
          </button>
          <button
            type="button"
            className="flex flex-col items-center pb-1.5 pt-3"
          >
            <span>V</span>
            <span className="mt-3 flex h-8 w-8 items-center justify-center rounded-full text-base font-semibold text-gray-900">
              21
            </span>
          </button>
          <button
            type="button"
            className="flex flex-col items-center pb-1.5 pt-3"
          >
            <span>S</span>
            <span className="mt-3 flex h-8 w-8 items-center justify-center rounded-full bg-gray-900 text-base font-semibold text-white">
              22
            </span>
          </button>
          <button
            type="button"
            className="flex flex-col items-center pb-1.5 pt-3"
          >
            <span>D</span>
            <span className="mt-3 flex h-8 w-8 items-center justify-center rounded-full text-base font-semibold text-gray-900">
              23
            </span>
          </button>
          <button
            type="button"
            className="flex flex-col items-center pb-1.5 pt-3"
          >
            <span>L</span>
            <span className="mt-3 flex h-8 w-8 items-center justify-center rounded-full text-base font-semibold text-gray-900">
              24
            </span>
          </button>
          <button
            type="button"
            className="flex flex-col items-center pb-1.5 pt-3"
          >
            <span>M</span>
            <span className="mt-3 flex h-8 w-8 items-center justify-center rounded-full text-base font-semibold text-gray-900">
              25
            </span>
          </button>
        </div>
        <div className="flex w-full flex-auto">
          <div className="w-14 flex-none bg-white ring-1 ring-gray-100" />
          <div className="grid flex-auto grid-cols-1 grid-rows-1">
            {/* Horizontal lines */}
            <div
              className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100"
              style={{ gridTemplateRows: "repeat(48, minmax(3.5rem, 1fr))" }}
            >
              <div ref={containerOffset} className="row-end-1 h-7"></div>
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  12AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  1AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  2AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  3AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  4AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  5AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  6AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  7AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  8AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  9AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  10AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  11AM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  12PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  1PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  2PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  3PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  4PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  5PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  6PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  7PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  8PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  9PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  10PM
                </div>
              </div>
              <div />
              <div>
                <div className="sticky left-0 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                  11PM
                </div>
              </div>
              <div />
            </div>

            {/* Events */}
            <ol
              className="col-start-1 col-end-2 row-start-1 grid grid-cols-1"
              style={{
                gridTemplateRows: "1.75rem repeat(288, minmax(0, 1fr)) auto",
              }}
            >
              {days
                ?.filter((days) => days.isCurrentDay)
                .map((day) => {
                  return items[day.key]?.map((item) => {
                    const dayStartTime = DateTime.fromFormat(
                      "00:00:00",
                      "HH:mm:ss"
                    );
                    const itemStartTime = DateTime.fromFormat(
                      item.startTime,
                      "HH:mm:ss"
                    );

                    const itemStartTimeMinutes = Math.abs(
                      dayStartTime.diff(itemStartTime).as("minutes")
                    );

                    const isUser = item.users?.find(
                      (user) => user.id === Number(id)
                    )
                      ? true
                      : false;

                    return (
                      <li
                        onClick={() => onEventClick(item)}
                        className="relative mt-px flex"
                        style={{
                          gridRow: `${itemStartTimeMinutes / 5 + 2} / span ${Math.max(
                            item.duration / 5,
                            3 // Asegura un tamaño mínimo de 3 filas para eventos cortos
                          )}`,
                          minHeight: "2rem", // Tamaño mínimo absoluto en píxeles
                        }}
                      >
                        <a
                          href="#"
                          style={{
                            backgroundColor: day.color
                              ? Color(day.color).alpha(0.75).hexa()
                              : colors[0].color,
                          }}
                          className="group absolute inset-1 flex items-center overflow-hidden rounded-lg p-2 text-xs leading-5 hover:bg-pink-100"
                          title={`${item.name} (${item.startTime} - ${item.endTime})`} // Tooltip para eventos cortos
                        >
                          <div
                            className="flex-shrink-0 w-2 h-2 rounded-full mr-2"
                            style={{
                              backgroundColor: isUser
                                ? "#7CFC00"
                                : Color(item.color).darken(0.5).hexa(),
                            }}
                          ></div>
                          <p
                            className="font-semibold truncate"
                            style={{
                              color: Color(item.color).darken(0.5).hexa(),
                            }}
                          >
                            {item.name}
                          </p>
                          <span
                            className="ml-auto text-gray-500 text-[10px]"
                            style={{
                              color: Color(item.color).darken(0.3).hexa(),
                            }}
                          >
                            {item.startTime} - {item.endTime}
                          </span>
                        </a>
                      </li>
                    );
                  });
                })}
            </ol>
          </div>
        </div>
      </div>
      <div className="hidden w-1/2 max-w-md flex-none border-l border-gray-100 px-8 py-10 md:block">
        <div className="flex items-center text-center text-gray-900">
          <button
            onClick={onPrevMonth}
            type="button"
            className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
          >
            <span className="sr-only">Mes anterior</span>
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          <div className="flex-auto text-sm font-semibold">
            {currentDateTime.setLocale("es").toFormat("MMMM yyyy")}
          </div>
          <button
            onClick={onNextMonth}
            type="button"
            className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
          >
            <span className="sr-only">Mes siguiente</span>
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <div className="mt-6 grid grid-cols-7 text-center text-xs leading-6 text-gray-500">
          <div>L</div>
          <div>M</div>
          <div>M</div>
          <div>J</div>
          <div>V</div>
          <div>S</div>
          <div>D</div>
        </div>
        <div className="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200">
          {days.map((day, dayIdx) => (
            <button
              key={day.key}
              type="button"
              onClick={() => setCurrentDateTime(day.date)}
              className={classNames(
                "py-1.5 hover:bg-gray-100 focus:z-10",
                day.isCurrentMonth ? "bg-white" : "bg-gray-50",
                (day.isSelected || day.isCurrentDay) && "font-bold",
                day.isSelected && "text-white",
                !day.isSelected &&
                  day.isCurrentMonth &&
                  !day.isToday &&
                  "text-gray-900",
                !day.isSelected &&
                  !day.isCurrentMonth &&
                  !day.isToday &&
                  "text-gray-400",
                day.isCurrentDay && "text-primary",
                day.isToday && "!bg-primary",
                day.isToday && "!text-white",
                dayIdx === 0 && "rounded-tl-lg",
                dayIdx === 6 && "rounded-tr-lg",
                dayIdx === days.length - 7 && "rounded-bl-lg",
                dayIdx === days.length - 1 && "rounded-br-lg"
              )}
            >
              <time
                dateTime={day.key}
                className={classNames(
                  "mx-auto flex h-7 w-7 items-center justify-center rounded-full",
                  day.isSelected && day.isToday && "bg-primary-600",
                  day.isSelected && !day.isToday && "bg-gray-900"
                )}
              >
                {day.number}
              </time>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
